import React, { useState, useEffect, createContext, useContext } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { isTenantAuthenticated, tenantLogout } from "./TenantAuth";
import { TenantStyles } from "../styles/tenantStyles";

export const TenantHeaderContext = createContext({
  headerConfig: { title: "", breadcrumb: "", actions: null },
  setHeaderConfig: () => {},
});

export function useTenantHeader() {
  return useContext(TenantHeaderContext);
}

// ─── Route guard ─────────────────────────────────────────────
export function TenantProtectedRoute({ children }) {
  const navigate = useNavigate();
  useEffect(() => {
    if (isTenantAuthenticated()) return;
    // A signed-in admin who lands on a tenant page goes to the admin dashboard.
    let isAdmin = false;
    try { isAdmin = !!JSON.parse(localStorage.getItem("zangon_session") || "null")?.accessToken; } catch {}
    navigate(isAdmin ? "/dashboard" : "/sign-in", { replace: true });
  }, [navigate]);
  if (!isTenantAuthenticated()) return null;
  return children;
}

// ─── Main layout ─────────────────────────────────────────────
export default function TenantLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const isDashboard = path === "/tenant/dashboard" || path === "/tenant" || path.endsWith("/dashboard");

  const [headerConfig, setHeaderConfig] = useState({
    title: "",
    breadcrumb: "",
    actions: null,
  });

  let defaultTitle = "Dashboard";
  let defaultBreadcrumb = "Dashboard";

  if (path.includes("/buildings")) {
    defaultTitle = "Building Management";
    defaultBreadcrumb = "Buildings";
  } else if (path.includes("/dependants")) {
    defaultTitle = "Dependant Management";
    defaultBreadcrumb = "Dependants";
  } else if (path.includes("/cars")) {
    defaultTitle = "Car Management";
    defaultBreadcrumb = "Car Management";
  } else if (path.includes("/access-codes")) {
    defaultTitle = "Access Code Management";
    defaultBreadcrumb = "Access Codes";
  } else if (path.includes("/maintenance")) {
    defaultTitle = "Maintenance Management";
    defaultBreadcrumb = "Maintenance";
  } else if (path.includes("/payments")) {
    defaultTitle = "Payment Management";
    defaultBreadcrumb = "Payments";
  } else if (path.includes("/occupants")) {
    defaultTitle = "Occupant Management";
    defaultBreadcrumb = "Occupants";
  } else if (path.includes("/profile")) {
    defaultTitle = "My Profile";
    defaultBreadcrumb = "Profile";
  }

  const currentTitle = headerConfig.title || defaultTitle;
  const currentBreadcrumb = headerConfig.breadcrumb || defaultBreadcrumb;

  const handleLogout = async () => {
    try {
      await tenantLogout();
    } finally {
      navigate("/sign-in", { replace: true });
    }
  };

  useEffect(() => {
    const body = document.body;
    body.id = "kt_app_body";
    body.classList.add("app-default");
    body.setAttribute("data-kt-app-toolbar-enabled", "true");
    [
      "data-kt-app-header-fixed-mobile",
      "data-kt-app-sidebar-enabled",
      "data-kt-app-sidebar-fixed",
      "data-kt-app-sidebar-push-header",
      "data-kt-app-sidebar-push-toolbar",
      "data-kt-app-sidebar-push-footer",
    ].forEach((attribute) => body.removeAttribute(attribute));

    const html = document.documentElement;
    const saved = localStorage.getItem("kt_theme_mode") || "light";
    html.setAttribute("data-bs-theme", saved);

    const init = () => {
      window.KTThemeMode?.init?.();
      window.KTComponents?.init?.();
      window.KTMenu?.createInstances?.();
      window.KTDrawer?.createInstances?.();
      window.KTScroll?.createInstances?.();
      window.KTScrollTop?.createInstances?.();
    };
    init();
    const t = setTimeout(init, 0);
    return () => clearTimeout(t);
  }, []);

  return (
    <TenantHeaderContext.Provider value={{ headerConfig, setHeaderConfig }}>
      <TenantStyles />
      <div className="d-flex flex-column flex-root app-root d6-tenant-app d6-tenant-topnav d6-topnav-shell" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">
          {/* Header topbar - renders at top:0 on all pages */}
          <div
            id="kt_app_header"
            className="app-header"
            data-kt-sticky="true"
            data-kt-sticky-activate="{default: false, lg: true}"
            data-kt-sticky-name="app-header-sticky"
            data-kt-sticky-offset="{default: false, lg: '300px'}"
          >
            <div className="app-container container-fluid d6-topnav-bar d6-tenant-topnav-bar" id="kt_app_header_container">
              <NavLink to="/tenant/dashboard" className="d6-topnav-brand" aria-label="Matilda home">
                <img alt="Matilda" src="/assets/media/zangon-fav.png" />
              </NavLink>
              {isDashboard ? (
                headerConfig.searchComponent || (
                  <form className="d6-topnav-search" role="search" onSubmit={(e) => e.preventDefault()}>
                    <i className="ki-outline ki-magnifier" aria-hidden="true"></i>
                    <input
                      type="search"
                      aria-label="Search the system"
                      placeholder="Search homes, people, payments..."
                      readOnly
                    />
                    <kbd>⌘ K</kbd>
                  </form>
                )
              ) : (
                <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3 mb-0 d6-tenant-page-title">
                  <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 mb-1">
                    <li className="breadcrumb-item fw-bold lh-1">
                      <NavLink className="me-1" to="/tenant/dashboard" aria-label="Tenant dashboard">
                        <i className="ki-outline ki-home fs-6" />
                      </NavLink>
                    </li>
                    <li className="breadcrumb-item"><i className="ki-outline ki-right fs-7 mx-n1" /></li>
                    <li className="breadcrumb-item fw-bold lh-1">{currentBreadcrumb}</li>
                  </ul>
                  <h1 className="fw-bolder m-0 fs-2">{currentTitle}</h1>
                </div>
              )}
              <nav className="d6-topnav-links" aria-label="Main navigation">
                <NavLink to="/tenant/dashboard" end>My Dashboard</NavLink>
                <NavLink to="/tenant/buildings">My Homes</NavLink>
                <NavLink to="/tenant/dependants">My Household</NavLink>
                <NavLink to="/tenant/cars">My Vehicles</NavLink>
                <NavLink to="/tenant/payments">My Payments</NavLink>
                <NavLink to="/tenant/maintenance">My Maintenance</NavLink>
                <NavLink to="/tenant/visits">My Visitors</NavLink>
              </nav>
              <div className="d6-topnav-actions">
                <NavLink to="/tenant/profile" className="d6-topnav-icon" aria-label="Profile" title="Profile"><i className="ki-outline ki-user" /></NavLink>
                <button type="button" className="d6-topnav-icon d6-topnav-logout" onClick={handleLogout} aria-label="Log out" title="Log out"><i className="ki-outline ki-entrance-left" /></button>
              </div>
            </div>
          </div>

          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            {/* ── Main content ── */}
            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              {/* Page content */}
              <div className="app-content flex-column-fluid" id="kt_app_content">
                <Outlet />
              </div>

              {/* Footer */}
              <div className="app-footer align-items-center justify-content-center d-flex py-4 border-top border-gray-200">
                <span className="text-gray-500 fw-semibold fs-8">
                  2026©{" "}
                  <a href="https://pixels.com.ng/" target="_blank" rel="noreferrer" className="text-gray-500 text-hover-success">
                    Powered by Pixels Solutions Limited
                  </a>
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </TenantHeaderContext.Provider>
  );
}
